import { Div, Text } from "atomize"
import { useContext, useEffect, useState } from "react"
import { lessonsContext } from "./providers/lessons.provider"
import Lesson from "./Lesson"
import { useJustFinished } from "./lesson/JustFinished"
import { motion } from "framer-motion"
import { profileContext } from "./providers/profile.provider"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser, faCalendarPlus } from "@fortawesome/free-solid-svg-icons"
import { theme } from "./settings/config"
import { isDesktopLike } from "./atoms/DesktopLikeView"
import { playersContext } from "./providers/players.provider"
import { modalContext } from "./providers/modal.provider"
import { schoolContext } from "./providers/school.provider"
import EmptyState from "./atoms/EmptyState"
import LessonsSkeleton from "./atoms/LessonsSkeleton"
import ElsewhereLessonRow from "./week/ElsewhereLessonRow"
import MatchRow from "./week/MatchRow"
import { isMatch } from "./utils/match.utils"
import {
	getLessonOverlaps,
	groupLessonsElsewhere,
	isElsewhere,
	withLessonsElsewhere,
} from "./utils/week.utils"
import { teachesInSchool } from "./utils/func.utils"

function LessonsList({ selectedDate, lessons, lessonsCoach, lessonsCoachID }) {
	const { coach, loading: profileLoading } = useContext(profileContext)
	const { isLoading, lessonToOpen, setLessonToOpen, elsewhereLessons } =
		useContext(lessonsContext)
	const { activePlayers, loadedSchool } = useContext(playersContext)
	const { getSelectedSchool, selectedSchool, loading: schoolLoading } =
		useContext(schoolContext)
	const Modal = useContext(modalContext)
	const [isOpen, setOpenLesson] = useState(null)

	// All'avvio l'app attraversa parecchi stati prima di sapere qualcosa, e
	// gli effetti di React girano *dopo* che lo schermo è stato disegnato:
	// chiedere "stai caricando?" a un provider che non ha ancora cominciato
	// risponde di no, e per un fotogramma la schermata dice la cosa
	// sbagliata. Da lì il balletto tra forma e invito da principiante.
	//
	// Quindi non si guarda se qualcuno sta caricando, ma se le prove ci
	// sono: chi siamo, quale circolo, e gli allievi di *questo* circolo già
	// arrivati. Un maestro senza nessun circolo è l'unico caso in cui si
	// decide senza aspettare, perché non c'è niente da aspettare.
	const school = getSelectedSchool()
	const isBooting =
		profileLoading ||
		!coach ||
		schoolLoading ||
		(Boolean(school) && (loadedSchool !== selectedSchool || isLoading))

	// Un giorno senza lezioni è una cosa; un'app dove non è mai successo
	// niente è un'altra. Chi non ha nemmeno un allievo è appena arrivato, e
	// va accompagnato alla prima mossa invece che rassicurato.
	// chi qui non insegna non ha una prima lezione da segnare
	const isNewHere = activePlayers.length === 0 && teachesInSchool(coach, selectedSchool)

	// Le lezioni di un maestro che ha chiuso l'account restano al circolo —
	// ci sono attaccati pagamenti e presenze di allievi che frequentano
	// ancora — ma il suo documento non c'è più. Senza un nome da mettere qui
	// finivano sotto il nome di chi sta guardando: lezioni di un altro
	// attribuite a te. Sono successe, chi le ha date se n'è andato, e l'app
	// lo dice invece di far finta di niente.
	const columnName = () => {
		if (lessonsCoach?.coachName) return lessonsCoach.coachName
		if (lessonsCoachID && lessonsCoachID !== coach.coachID) return "Non più nel circolo"
		return coach.coachName
	}

	// Dalla settimana si arriva qui toccando una lezione: la si trova già
	// aperta e sotto gli occhi, come se la si fosse toccata qui.
	useEffect(() => {
		if (!lessonToOpen) return
		if (!lessons?.some(lesson => `${lesson.coachID}_${lesson.lessonID}` === lessonToOpen))
			return

		setOpenLesson(lessonToOpen)
		setLessonToOpen(null)
		// dopo l'animazione di apertura, altrimenti si scorre verso un punto
		// che si sta ancora spostando
		setTimeout(
			() =>
				document
					.getElementById(`lesson_${lessonToOpen}`)
					?.scrollIntoView({ behavior: "smooth", block: "center" }),
			400,
		)
	}, [lessonToOpen, lessons, setLessonToOpen])

	const handleOpenLessonActions = lessonID => {
		isOpen === null || isOpen !== lessonID ? setOpenLesson(lessonID) : setOpenLesson(null)
	}

	// Le ore negli altri circoli, se si è scelto di vederle: solo nella
	// colonna delle proprie lezioni, in mezzo alle altre al loro orario.
	const elsewhere =
		lessonsCoachID && lessonsCoachID === coach?.coachID
			? elsewhereLessons[selectedDate] ?? []
			: []
	const shownLessons = withLessonsElsewhere(lessons, elsewhere)
	// una lezione che comincia quando finisce quella prima le sta attaccata;
	// quelle di seguito in un altro circolo fanno un blocco solo
	const items = groupLessonsElsewhere(shownLessons, selectedSchool)
	// due lezioni che si accavallano, anche con una altrove, lo dicono
	const overlaps = getLessonOverlaps(shownLessons)
	// in ogni colonna, la sua ultima lezione appena finita
	const justFinished = useJustFinished(lessons)

	return (
		<motion.div
			key={selectedDate}
			animate={{ y: [20, 0] }}
			transition={{
				ease: [0.04, 0.62, 0.23, 0.98],
				duration: 0.4,
			}}>
			<Div m={{ t: "0.5rem" }}>
				{isBooting ? (
					<LessonsSkeleton />
				) : shownLessons.length > 0 ? (
					<>
						{isDesktopLike && (
							<Div
								d="flex"
								w="fit-content"
								align="center"
								style={{ gap: "0.25rem" }}
								m={{ t: "1.5rem" }}
								p="0.5rem 1.5rem"
								rounded="15px"
								shadow={theme.shadows.accent}
								bg={theme.colors.surface}>
								<FontAwesomeIcon icon={faUser} />
								<Text textSize="subheader">{columnName()}</Text>
							</Div>
						)}
						{items.map(({ lessons: group, joins, index, lesson = group[0] }) =>
							isElsewhere(lesson, selectedSchool) ? (
								<ElsewhereLessonRow
									key={`${lesson.schoolID}_${lesson.lessonID}`}
									lessons={group}
									m={{ y: "1rem" }}
								/>
							) : isMatch(lesson) ? (
								<MatchRow
									key={lesson.lessonID}
									lesson={lesson}
									joins={joins}
									m={{
										t: joins.joinsPrev ? "0.25rem" : "1rem",
										b: joins.joinsNext ? "0.25rem" : "1rem",
									}}
								/>
							) : (
								<div
									key={lesson.lessonID}
									id={`lesson_${lesson.coachID}_${lesson.lessonID}`}>
									<Lesson
										lessonData={lesson}
										lessonCoach={lessonsCoach}
										lessonIndex={index}
										isNextLessonConsecutive={joins.joinsNext}
										isPrevLessonConsecutive={joins.joinsPrev}
										isOpen={isOpen === `${lesson.coachID}_${lesson.lessonID}`}
										modalView={false}
										handleOpenLessonActions={handleOpenLessonActions}
										justFinishedLabel={
											justFinished?.lessonID === lesson.lessonID
												? justFinished.label
												: null
										}
										overlapsWith={overlaps.get(lesson.lessonID)}
									/>
								</div>
							),
						)}
					</>
				) : isNewHere ? (
					<EmptyState
						icon={faCalendarPlus}
						title="Qui ci vanno le tue lezioni"
						text="Segna la prima: scegli l'ora, chi c'era e come ha pagato. Presenze, incassi e calendario si aggiornano da soli."
						actionText="Aggiungi una lezione"
						color={theme.colors.primary}
						action={() =>
							getSelectedSchool() && Modal.open("newLesson", selectedDate)
						}
					/>
				) : (
					<Text textSize="subheader" textAlign="center" p="0.5rem 1rem">
						RELAX, non hai lezioni in questo giorno! 😌
					</Text>
				)}
			</Div>
		</motion.div>
	)
}

export default LessonsList
